import React, { useState } from 'react';
import { HiOutlineCheckBadge, HiOutlineTrash } from 'react-icons/hi2';
import { useAgents } from '../../../../hooks';
import { DAV_APIS } from '../../../../adapters';
import { AgentProps } from '../../../../constants';
import { Alert, Table, Modal } from '../../../commons';
import { agentColumns } from '../../../commons/Table';
import { Card } from '../../../ui/Card';
import AgentEditModal from './AgentEditModal';

const Agents = () => {
  const [refresh, setRefresh] = useState<number | null>(null);
  const [selectedAgent, setSelectedAgent] = useState({} as AgentProps);
  const [showEditModal, setShowEditModal] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const { agents, loading } = useAgents(refresh);

  const handleRefresh = () => setRefresh(Math.random());

  const onViewHandler = (e: any, agent: AgentProps) => {
    e.stopPropagation();
    setSelectedAgent(agent);
    setModalMode('view');
    setShowEditModal(true);
  };

  const onEditHandler = (e: any, agent: AgentProps) => {
    e.stopPropagation();
    setSelectedAgent(agent);
    setModalMode('edit');
    setShowEditModal(true);
  };

  const onDeleteHandler = (e: any, agent: AgentProps) => {
    e.stopPropagation();
    setSelectedAgent(agent);
    setShowDeleteModal(true);
  };

  const handleDeleteAgent = async () => {
    setIsLoading(true);
    try {
      const res = await DAV_APIS.agents.deleteAgent(selectedAgent.id);
      if (res.status === 204) {
        Alert({ message: 'Agent deleted successfully', variant: 'success' });
        handleRefresh();
        setShowDeleteModal(false);
      }
    } catch (error) {
      Alert({ message: 'Failed to delete agent', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700'>
      {/* Page Header */}
      <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight'>
            Agent Management
          </h1>
          <p className='text-gray-500 dark:text-gray-400 mt-1 max-w-2xl'>
            Review and manage your network of travel agents and partners.
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <div className='flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/10 text-primary-600 dark:text-primary-400 rounded-lg text-sm font-medium'>
            <HiOutlineCheckBadge className='w-4 h-4' />
            {agents.filter(a => a.isActive).length} Verified
          </div>
        </div>
      </div>

      <Card variant='glass' className='overflow-hidden border-none shadow-xl shadow-gray-200/50 dark:shadow-none'>
        {/* @ts-ignore */}
        <Table
          columns={agentColumns}

          data={agents}
          loading={loading}
          onEditHandler={onEditHandler}
          onDeleteHandler={onDeleteHandler}
          onViewHandler={onViewHandler}
          viewExternal={false}
          recordName='Agents'
          placeholder='Search agents...'
        />
      </Card>

      {/* Edit Agent Modal */}
      {selectedAgent.id && (
        <AgentEditModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          agent={selectedAgent}
          mode={modalMode}
          onSuccess={handleRefresh}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        size='sm'
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSuccess={handleDeleteAgent}
        loading={isLoading}
        successText='Delete Agent'
        loadingText='Deleting...'
      >
        <div className='space-y-3'>
          <div className='w-12 h-12 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center text-red-600 mb-4'>
            <HiOutlineTrash className='w-6 h-6' />
          </div>
          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>Remove Agent</h2>
          <p className='text-gray-600 dark:text-gray-400 leading-relaxed text-sm'>
            Are you sure you want to remove <span className='font-bold text-gray-900 dark:text-white'>{selectedAgent?.username}</span> from the platform? This action cannot be undone and all associated data will be lost.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Agents;
